/**
 * The customer_request route: the homeowner asks their utility for their own
 * interval data, from their own email, and uploads the file the utility sends
 * back. Burlington Electric Department asked for this (2026-10-05): it wants
 * every AMI data request to come from its customer, not from TrueLoad.
 *
 * The homeowner page writes the request for them. This module is the one
 * place its wording lives, so the page, its copy-and-paste fallback and the
 * tests read the same text.
 */

export interface CustomerRequestInput {
  utilityName: string;
  customerName: string;
  /** The service address the contractor entered on the project. */
  serviceAddress: string;
  /** As stored on the request. For BED, "CustomerID-LocationID", as printed on the bill. */
  accountNumber: string | null | undefined;
  contractorName: string;
  companyName: string;
}

export interface CustomerRequestEmail {
  subject: string;
  body: string;
}

/** True for Burlington Electric Department, whose account number is two IDs. */
export function isBurlingtonElectric(utilityName: string | null | undefined): boolean {
  return (utilityName ?? '').includes('Burlington Electric');
}

/**
 * The account lines for the request. BED prints its account as
 * "CustomerID-LocationID" and routes AMI requests by the Location ID, so the
 * two are named separately; anything else is one account number.
 */
export function accountLines(utilityName: string, accountNumber: string | null | undefined): string[] {
  const value = (accountNumber ?? '').trim();
  if (!value) return [];
  if (isBurlingtonElectric(utilityName)) {
    const dash = value.indexOf('-');
    const customerId = (dash === -1 ? value : value.slice(0, dash)).trim();
    const locationId = (dash === -1 ? '' : value.slice(dash + 1)).trim();
    const lines: string[] = [];
    if (customerId) lines.push(`Customer ID: ${customerId}`);
    if (locationId) lines.push(`Location ID: ${locationId}`);
    return lines;
  }
  return [`Account number: ${value}`];
}

/**
 * How the Get data page stores BED's two IDs as one account number. The dash
 * stays whenever a Location ID is given, so a Location ID on its own is never
 * read back as a Customer ID.
 */
export function joinBedAccount(customerId: string, locationId: string): string | null {
  const c = customerId.trim();
  const l = locationId.trim();
  if (!c && !l) return null;
  return l ? `${c}-${l}` : c;
}

export function buildCustomerRequestEmail(input: CustomerRequestInput): CustomerRequestEmail {
  const utility = input.utilityName.trim() || 'my electric utility';
  const name = input.customerName.trim();
  const address = input.serviceAddress.trim();
  const contractor = [input.contractorName.trim(), input.companyName.trim()].filter(Boolean).join(' of ');

  const details = [
    name ? `Name: ${name}` : '',
    address ? `Service address: ${address}` : '',
    ...accountLines(utility, input.accountNumber),
  ].filter(Boolean);

  const body = [
    `Hello ${utility},`,
    '',
    'I am requesting a copy of the 15-minute interval usage data from my electric meter, covering more than the last 12 months (13 months or longer if you have it). A CSV or Excel file is ideal.',
    '',
    ...details,
    '',
    `${contractor || 'My electrical contractor'} needs the data to evaluate my home's existing electrical service capacity under the National Electrical Code (NEC 220.87). Please send the file to me by replying to this email.`,
    '',
    'Thank you,',
    name || '',
  ]
    .join('\n')
    .replace(/\n+$/, '');

  return {
    subject: `Request for my 15-minute interval usage data${address ? ` – ${address}` : ''}`,
    body,
  };
}

/** A mailto: link carrying the request. Empty `to` still opens a draft. */
export function mailtoHref(to: string, email: CustomerRequestEmail): string {
  return `mailto:${encodeURIComponent(to.trim()).replace(/%40/g, '@')}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
}
